import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IndustryConfigFactory } from '../app/config/industry/industry-config.factory';
import { IndustryConfig } from '../app/config/industry/industry-config.interface';
import { IndustryType, getIndustryName } from '../app/config/industry/industry-types.enum';
import { isDenominationEnabledFor, isLoyaltyEnabledFor, isPettyCashEnabledFor } from '../app/config/feature-flags';

/**
 * Industry Service
 * Manages industry-specific configurations and provides helper methods
 * for feature detection and API path transformation
 */
@Injectable({
    providedIn: 'root',
})
export class IndustryService {
    private readonly STORAGE_KEY = 'pos_industry';

    private industrySubject: BehaviorSubject<number>;
    public industry$: Observable<number>;
    private currentConfig: IndustryConfig;

    constructor() {
        // Initialize with stored industry or default to RETAIL
        const storedIndustry = this.getStoredIndustry();
        this.industrySubject = new BehaviorSubject<number>(storedIndustry);
        this.industry$ = this.industrySubject.asObservable();
        this.currentConfig = IndustryConfigFactory.getConfig(storedIndustry);
    }

    /**
     * Set the current industry type
     * @param industry - Industry type code from login response
     */
    setIndustry(industry: number | string): void {
        const id = industry != null ? Number(industry) : 0;
        if (id === this.industrySubject.value) {
            return; // No change needed
        }

        console.log(`Setting industry to: ${id} (${getIndustryName(id)})`);

        // Update configuration
        this.currentConfig = IndustryConfigFactory.getConfig(id);

        // Store in localStorage
        localStorage.setItem(this.STORAGE_KEY, id.toString());

        // Notify subscribers
        this.industrySubject.next(id);
    }

    /**
     * Get the current industry type
     * @returns Current industry type code
     */
    getIndustry(): number {
        return this.industrySubject.value;
    }

    /**
     * Get the current industry configuration
     * @returns IndustryConfig object
     */
    getConfig(): IndustryConfig {
        return this.currentConfig;
    }

    /**
     * Check if current industry is Mobile
     * @returns true if industry is Mobile
     */
    isMobile(): boolean {
        return  Number(this.industrySubject.value) === IndustryType.MOBILE;
    }

    /**
     * Check if Cash Denomination is available for the current industry
     * (controlled by DENOMINATION_HIDDEN_INDUSTRIES in app/config/feature-flags.ts)
     */
    isDenominationEnabled(): boolean {
        return isDenominationEnabledFor(this.industrySubject.value);
    }

    /**
     * Check if Petty Cash is available for the current industry
     * (controlled by PETTY_CASH_HIDDEN_INDUSTRIES in app/config/feature-flags.ts)
     */
    isPettyCashEnabled(): boolean {
        return isPettyCashEnabledFor(this.industrySubject.value);
    }

    /**
     * Whether Loyalty is available for the current industry (see LOYALTY_HIDDEN_INDUSTRIES)
     */
    isLoyaltyEnabled(): boolean {
        return isLoyaltyEnabledFor(this.industrySubject.value);
    }

    /**
     * Check if current industry is Retail
     * @returns true if industry is Retail
     */
    isRetail(): boolean {
        return this.industrySubject.value === IndustryType.RETAIL;
    }

    /**
     * Check if current industry is Restaurant
     * @returns true if industry is Restaurant
     */
    isRestaurant(): boolean {
        return this.industrySubject.value === IndustryType.RESTAURANT;
    }

    /**
     * Check if current industry is Pharmacy
     * @returns true if industry is Pharmacy
     */
    isPharmacy(): boolean {
        return Number(this.industrySubject.value) === IndustryType.PHARMACY;
    }

    /**
     * Generic industry check — use this for all future verticals.
     * Avoids adding a new isXxx() method per industry.
     *
     * @example
     * // In a component/service:
     * this.industryService.isIndustry(IndustryType.PHARMACY)
     *
     * // In a template (expose IndustryType on the component class first):
     * *ngIf="industryService.isIndustry(IndustryType.PHARMACY)"
     *
     * @param type - IndustryType enum value to compare against
     * @returns true if current industry matches the given type
     */
    isIndustry(type: IndustryType): boolean {
        return Number(this.industrySubject.value) === type;
    }

    /**
     * Check if a specific feature is enabled
     * @param featureName - Name of the feature to check
     * @returns true if feature is enabled for current industry
     */
    isFeatureEnabled(featureName: keyof IndustryConfig['features']): boolean {
        return this.currentConfig.features[featureName] === true;
    }

    /**
     * Transform API path based on current industry
     * Adds industry-specific segment to the path if required
     * @param path - Original API path (e.g., '/api/v1/order/...' or '/v1/sale/...')
     * @returns Transformed path (e.g., '/api/v1/mobile/order/...' or '/v1/mobile/sale/...')
     */
    getApiPath(path: string): string {
        const segment = this.currentConfig.apiPathSegment;

        if (!segment) {
            return path; // No transformation needed (Retail)
        }

        // Normalize path - ensure it starts with /
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;

        // Pattern 1: /api/v1/... -> /api/v1/mobile/...
        const apiV1Pattern = /^(\/api\/v\d+)\//i;
        const apiMatch = normalizedPath.match(apiV1Pattern);

        if (apiMatch) {
            const prefix = apiMatch[1]; // e.g., '/api/v1'
            const restOfPath = normalizedPath.substring(prefix.length + 1); // Skip the trailing /
            return `${prefix}/${segment}/${restOfPath}`;
        }

        // Pattern 2: /v1/... -> /v1/mobile/...
        const v1Pattern = /^(\/(v\d+))\//i;
        const v1Match = normalizedPath.match(v1Pattern);

        if (v1Match) {
            const prefix = v1Match[1]; // e.g., '/v1'
            const restOfPath = normalizedPath.substring(prefix.length + 1); // Skip the trailing /
            return `${prefix}/${segment}/${restOfPath}`;
        }

        // If no recognizable pattern, just add segment after first slash
        return normalizedPath.replace(/^\//, `/${segment}/`);
    }

    /**
     * Transform payload with industry-specific fields
     * @param payload - Original payload object
     * @param additionalFields - Additional fields to merge (industry-specific)
     * @returns Transformed payload
     */
    transformPayload(payload: any, additionalFields?: any): any {
        if (!additionalFields || Object.keys(additionalFields).length === 0) {
            return payload;
        }

        return {
            ...payload,
            ...additionalFields,
        };
    }

    /**
     * Get industry name for display
     * @returns Human-readable industry name
     */
    getIndustryName(): string {
        return getIndustryName(this.industrySubject.value);
    }

    /**
     * Clear stored industry (useful for logout).
     * Resets to 0 — a sentinel "no industry selected" state.
     * Using 0 instead of IndustryType.RETAIL avoids the early-return
     * in setIndustry() when the next user logs in with the same industry
     * code (e.g., two consecutive logins as industry 4).
     */
    clearIndustry(): void {
        localStorage.removeItem(this.STORAGE_KEY);
        // Force reset to 0 so next setIndustry() always fires,
        // even if the new user has the same industry as the old one.
        this.industrySubject.next(0);
        this.currentConfig = IndustryConfigFactory.getConfig(IndustryType.RETAIL);
    }

    /**
     * Get stored industry from localStorage
     * Checks 'pos_industry' key first (set on login/setIndustry).
     * Falls back to the 'name' session object (user.industry) so that
     * a page-refresh also gets the correct industry without a re-login.
     * @returns Stored industry code or default to RETAIL
     */
    private getStoredIndustry(): number {
        // Primary: dedicated key set by setIndustry()
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            const industry = parseInt(stored, 10);
            if (!isNaN(industry)) {
                return industry;
            }
        }

        // Fallback: read from the user session object saved during login
        try {
            const userSession = localStorage.getItem('name');
            if (userSession) {
                const user = JSON.parse(userSession);
                if (user?.industry != null) {
                    const industryFromSession = parseInt(user.industry, 10);
                    if (!isNaN(industryFromSession)) {
                        // Sync the dedicated key so future reads are fast
                        localStorage.setItem(this.STORAGE_KEY, industryFromSession.toString());
                        return industryFromSession;
                    }
                }
            }
        } catch {
            // JSON parse error — ignore and use default
        }

        return IndustryType.RETAIL; // Default
    }
}
